import {Types2D, TypesFlow, TypesSocial} from "@dp-instant/interfaces";
import {bindMediator, ProgressBar} from "@dp-instant/2d";
import {CANVAS_ID, lazyGet} from "@dp-instant/core";
import {LevelStartActionSIO} from "@game/actions/LevelStartActionSIO";
import {GameModel} from "@game/coregame/model/GameModel";
import {ContinentModel} from "@game/coregame/model/ContinentModel";
import {TypesGame} from "@game/TypesGame";
import {ContainerModule, decorate, injectable, interfaces} from "inversify";
import {InputManager} from "./managers/InputManager";
import {InputManagerBase} from "./managers/InputManagerBase";
import {FieldMediator} from "./mediators/FieldMediator";
import {ArrowsMediator} from "./mediators/ArrowsMediator";
import {ArrowsView} from "./view/ArrowsView";
import {ArrowView} from "./view/ArrowView";
import {FieldView} from "./view/FieldView";
import {StateShapeView} from "./view/StateShapeView";
import Root from "./view/Root";
import {CapitalView} from "./view/CapitalView";
import {Population} from "./view/Population";
import {BurstWaveAction} from "./actions/BurstWaveAction";
import {Building} from "@game/coregame/ecs/entities/Building";
import {Entity} from "tick-knock";
import {Field} from "@game/coregame/ecs/entities/Field";
import {Spawner} from "@game/coregame/view/Spawner";
import {Fighter} from "@game/coregame/ecs/entities/Fighter";
import {FighterView} from "@game/coregame/view/FighterView";
import {MetaModel} from "@game/coregame/model/MetaModel";
import {LevelRestartActionSIO} from "@game/actions/LevelRestartActionSIO";
import {LevelNextActionSIO} from "@game/actions/LevelNextActionSIO";
import {RootMediator} from "@game/coregame/mediators/RootMediator";
import {LevelEndActionSIO} from "@game/actions/LevelEndActionSIO";
import {BotLogic} from "./ecs/components/BotLogic";
import {BotCalculationLogic} from "./ecs/components/bots/BotCalculationLogic";
import {FighterDeathEffectAction} from "@game/coregame/actions/FighterDeathAction";
import {CookieModel} from "@game/coregame/model/CookieModel";
import {TutorialFingerView} from "./view/TutorialFingerView";
import {GiftTypeReward} from "@game/coregame/model/GiftModel";
import {IReferralRewardsModel, ReferralRewardsModel} from "@dp-instant/social";
import {Reward} from "@game/ui/interface/ILeaderBoardProps";
import {StageEndAction} from "@game/actions/StageEndAction";
import {PlayWithOpponentActionSIO} from "@game/actions/PlayWithOpponentSIO";
import {SubmitContextScoreAction} from "@game/actions/SubmitContextScoreAction";
import {SIOConstants} from "@game/constants";
import {GenerateShareImageAction} from "@game/actions/GenerateShareImageAction";
import {ScreenShotActionSIO} from "@game/coregame/actions/ScreenShotActionSIO";
import {SpritesPool} from "@game/coregame/utils/SpritesPool";
import {DestroyFieldAction} from "@game/coregame/actions/DestroyFieldAction";
import {GenerateMapShapeAction} from "@game/coregame/actions/GenerateMapShapeAction";
import {GenerateMapSpriteAction} from "@game/coregame/actions/GenerateMapSpriteAction";
import {SkinManager} from "@game/coregame/managers/SkinManager";
import {ShowGiftPopupAction} from "@game/actions/ShowGiftPopupAction";
import {ShowWinPopupAction} from "@game/actions/ShowWinPopupAction";
import {LevelCompletedPopupAction} from "@game/actions/LevelCompletedPopupAction";
import {BattleResultsPopupAction} from "@game/actions/BattleResultsPopupAction";
import {LoadLevelAction} from "@game/actions/LoadLevelAction";

import {StartGameAction} from "@game/actions/StartGameAction";
import {
    TournamentCreateAction,
    TournamentPostScoreAction,
    TournamentShareAction
} from "@game/actions/tournaments";
import {SetCalculatedConstantValuesAction} from "@game/actions/SetCalculatedConstantValuesAction";
import {BotPresetsModel} from "./model/BotPresetsModel";
import {UpdateBotLevelAction} from "@game/actions/UpdateBotLevelAction";

decorate(injectable(), Entity);
decorate(injectable(), InputManagerBase);
export const CoreGameModule = new ContainerModule(
    (bind: interfaces.Bind, unbind, isBound, rebind) => {
        bind(TypesGame.actions.startGame).to(StartGameAction);
        bind(TypesGame.actions.giftPopup).to(ShowGiftPopupAction);
        bind(TypesGame.actions.levelCompletePopup).to(LevelCompletedPopupAction);
        bind(TypesGame.actions.battleResultsPopup).to(BattleResultsPopupAction);
        // tournaments
        bind(TypesGame.actions.tournamentReShare).to(TournamentShareAction);
        bind(TypesGame.actions.tournamentCreate).to(TournamentCreateAction);
        bind(TypesGame.actions.tournamentPostScore).to(TournamentPostScoreAction);

        bind(TypesGame.actions.winPopup).to(ShowWinPopupAction);
        bind(TypesGame.actions.loadLevel).to(LoadLevelAction);
        bind(TypesGame.spritesPool).to(SpritesPool).inSingletonScope();
        bind(TypesGame.model).to(GameModel).inSingletonScope();
        bind(TypesGame.levelModel).to(ContinentModel);
        bind(TypesGame.botLogic).to(BotLogic);
        bind(TypesGame.botCalculationLogic).to(BotCalculationLogic);
        rebind(TypesFlow.LevelStart).to(LevelStartActionSIO);
        rebind(TypesFlow.LevelRestart).to(LevelRestartActionSIO);
        rebind(TypesFlow.LevelNext).to(LevelNextActionSIO);
        rebind(TypesFlow.LevelEnd).to(LevelEndActionSIO);
        rebind(TypesFlow.PlayWith).to(PlayWithOpponentActionSIO);
        bind(TypesGame.actions.endStage).to(StageEndAction);
        bind(TypesGame.actions.submitContextScore).to(SubmitContextScoreAction);

        bind(ProgressBar).toSelf();
        rebind(Types2D.rootView)
            .to(Root)
            .inSingletonScope()
            .onActivation(bindMediator(RootMediator));

        rebind(Types2D.screenShotAction).to(ScreenShotActionSIO);

        bind(DestroyFieldAction).toSelf();
        bind(FighterDeathEffectAction).toSelf();
        bind(TypesGame.actions.createMapPart).to(GenerateMapShapeAction);
        bind(TypesGame.actions.createMap).to(GenerateMapSpriteAction);
        bind(GenerateShareImageAction).toSelf();
        bind(MetaModel).toSelf().inSingletonScope();
        bind(TypesGame.cookieModel).to(CookieModel).inSingletonScope();
        bind(TutorialFingerView).toSelf();
        bind(Building).toSelf();
        bind(Field).toSelf();
        bind(Spawner).toSelf();
        bind(Fighter).toSelf();

        bind(SetCalculatedConstantValuesAction).toSelf();

        bind(TypesGame.views.fieldClass).to(FieldView).onActivation(bindMediator(FieldMediator));
        bind(TypesGame.views.fighter).to(FighterView);
        bind(TypesGame.views.state).to(CapitalView);
        bind(TypesGame.views.stateShape).to(StateShapeView);
        bind(TypesGame.views.population).to(Population);

        bind(TypesGame.views.arrow).to(ArrowView);
        bind(TypesGame.views.arrows).to(ArrowsView).onActivation(bindMediator(ArrowsMediator));

        bind(TypesGame.actions.burst).to(BurstWaveAction);

        if (__SOCIAL__ === "fb") {
            (() => {
                bind(TypesSocial.refRewardsModel).to(ReferralRewardsModel).inSingletonScope();
                const refsModel = lazyGet<IReferralRewardsModel<Reward>>(
                    TypesSocial.refRewardsModel
                );
                if (refsModel) {
                    refsModel.generateNext = () =>
                        <Reward>{
                            type: GiftTypeReward.COINS,
                            count: SIOConstants.COINS_REWARD_FOR_INVITE
                        };
                }
            })();
        }

        bind(TypesGame.skinManager).to(SkinManager).inSingletonScope();

        bind(TypesGame.inputManager)
            .to(InputManager)
            .inSingletonScope()
            .onActivation((_, item) =>
                (item as InputManager).init(document.getElementById(CANVAS_ID))
            );

        bind(TypesGame.botPresetsModel).to(BotPresetsModel).inSingletonScope();
        bind(TypesGame.actions.updateBotLevelAction).to(UpdateBotLevelAction).inSingletonScope();
    }
);
